import { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Typography,
  Button,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  DialogActions,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { PlayArrow as PlayIcon, Add as AddIcon } from '@mui/icons-material';
import api from '../services/api';
import toast from 'react-hot-toast';
import ConfigCard from '../components/AutoCommenter/ConfigCard';
import AccountFilterBar from '../components/common/AccountFilterBar';
import { useAutoCommenterWebSocket, type AutoCommenterProgress } from '../hooks/useAutoCommenterWebSocket';

export default function AutoCommenterPage() {
  const [configs, setConfigs] = useState<any[]>([]);
  const [accounts, setAccounts] = useState<any[]>([]);
  const [filterAccountId, setFilterAccountId] = useState('');
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [progressMap, setProgressMap] = useState<Record<number, AutoCommenterProgress>>({});
  const [formData, setFormData] = useState({
    accountId: '',
    name: '',
    keywords: '',
    templates: '',
    maxCommentsPerDay: 10,
    templateMode: 'random',
  });

  const loadData = async () => {
    setLoading(true);
    try {
      const [cfgRes, accRes] = await Promise.all([
        api.get('/auto-commenter'),
        api.get('/vk-accounts'),
      ]);
      setConfigs(cfgRes.data.data.configs);
      setAccounts(accRes.data.data.accounts);
    } catch (err) {
      toast.error('Не удалось загрузить данные');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  // WebSocket handlers
  const handleAction = useCallback((data: any) => {
    console.log('New comment action received:', data);

    // Update config stats
    setConfigs((prev) =>
      prev.map((cfg) =>
        cfg.id === data.configId
          ? { ...cfg, todayComments: cfg.todayComments + 1, totalComments: cfg.totalComments + 1 }
          : cfg
      )
    );

    // Add action to the list
    const addActionFn = (window as any)[`addCommenterActionToConfig${data.configId}`];
    if (addActionFn) {
      addActionFn({ ...data.action, vkData: data.vkData });
    }

    toast.success(`Новый комментарий: пост от ${data.vkData?.authorName || 'неизвестного автора'}`);
  }, []);

  const handleStatus = useCallback((data: any) => {
    console.log('Status update:', data);
    setConfigs((prev) =>
      prev.map((cfg) =>
        cfg.id === data.configId
          ? { ...cfg, todayComments: data.todayComments, isActive: data.isActive }
          : cfg
      )
    );
  }, []);

  const handleError = useCallback((data: any) => {
    console.log('Error:', data);
    toast.error(`Правило #${data.configId}: ${data.error}`);
  }, []);

  const handleProgress = useCallback((data: AutoCommenterProgress) => {
    console.log('Progress update:', data);
    setProgressMap((prev) => ({
      ...prev,
      [data.configId]: data,
    }));

    // Clear completed/error progress after 30 seconds
    if (data.phase === 'completed' || data.phase === 'error') {
      setTimeout(() => {
        setProgressMap((prev) => {
          const newMap = { ...prev };
          if (newMap[data.configId]?.phase === data.phase) {
            delete newMap[data.configId];
          }
          return newMap;
        });
      }, 30000);
    }
  }, []);

  useAutoCommenterWebSocket({
    onAction: handleAction,
    onStatus: handleStatus,
    onError: handleError,
    onProgress: handleProgress,
  });

  const handleCreate = async () => {
    try {
      // Parse templates (each line is a template)
      const templatesArray = formData.templates
        .split('\n')
        .map((t) => t.trim())
        .filter((t) => t.length > 0);

      if (templatesArray.length === 0) {
        toast.error('Нужен хотя бы один шаблон комментария');
        return;
      }

      await api.post('/auto-commenter', {
        accountId: parseInt(formData.accountId),
        name: formData.name,
        keywords: formData.keywords.split(',').map((k) => k.trim()),
        templates: templatesArray,
        maxCommentsPerDay: formData.maxCommentsPerDay,
        templateMode: formData.templateMode,
      });
      toast.success('Правило создано!');
      setOpen(false);
      setFormData({
        accountId: '',
        name: '',
        keywords: '',
        templates: '',
        maxCommentsPerDay: 10,
        templateMode: 'random',
      });
      loadData();
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Не удалось создать правило');
    }
  };

  const handleRunAll = async () => {
    setRunning(true);
    try {
      const res = await api.post('/auto-commenter/run-all');
      const { queued, total } = res.data.data;
      toast.success(`Запущено ${queued} из ${total} активных правил`);
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Не удалось запустить');
    } finally {
      setRunning(false);
    }
  };

  const handleDelete = (id: number) => {
    setConfigs((prev) => prev.filter((cfg) => cfg.id !== id));
  };

  const visibleConfigs = filterAccountId
    ? configs.filter((cfg) => String(cfg.accountId) === filterAccountId)
    : configs;
  const activeCount = configs.filter((cfg) => cfg.isActive).length;

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress color="secondary" />
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Автокомментатор</Typography>
        <Box display="flex" gap={2}>
          <Button
            variant="outlined"
            color="secondary"
            startIcon={running ? <CircularProgress size={20} color="inherit" /> : <PlayIcon />}
            onClick={handleRunAll}
            disabled={running || activeCount === 0}
          >
            Запустить все активные ({activeCount})
          </Button>
          <Button variant="contained" color="secondary" startIcon={<AddIcon />} onClick={() => setOpen(true)}>
            Создать правило
          </Button>
        </Box>
      </Box>

      <AccountFilterBar accounts={accounts} value={filterAccountId} onChange={setFilterAccountId} />

      {configs.length === 0 ? (
        <Alert severity="info">
          Пока нет ни одного правила. Создайте первое правило автокомментатора, чтобы начать.
        </Alert>
      ) : visibleConfigs.length === 0 ? (
        <Alert severity="info">Нет правил для выбранного аккаунта.</Alert>
      ) : (
        <Box>
          {visibleConfigs.map((cfg) => (
            <ConfigCard
              key={cfg.id}
              config={cfg}
              onUpdate={loadData}
              onDelete={handleDelete}
              progress={progressMap[cfg.id] || null}
            />
          ))}
        </Box>
      )}

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Новое правило автокомментатора</DialogTitle>
        <DialogContent>
          <TextField
            select
            fullWidth
            label="VK Аккаунт"
            value={formData.accountId}
            onChange={(e) => setFormData({ ...formData, accountId: e.target.value })}
            margin="normal"
            SelectProps={{ native: true }}
          >
            <option value="">Выберите аккаунт</option>
            {accounts.map((acc) => (
              <option key={acc.id} value={acc.id}>
                {acc.firstName} {acc.lastName}
              </option>
            ))}
          </TextField>

          <TextField
            fullWidth
            label="Название правила"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            margin="normal"
          />

          <TextField
            fullWidth
            label="Ключевые слова (через запятую)"
            value={formData.keywords}
            onChange={(e) => setFormData({ ...formData, keywords: e.target.value })}
            margin="normal"
            helperText="Комментируются посты, содержащие эти слова. Пример: маркетинг, бизнес"
          />

          <TextField
            fullWidth
            label="Шаблоны комментариев (по одному на строку)"
            value={formData.templates}
            onChange={(e) => setFormData({ ...formData, templates: e.target.value })}
            margin="normal"
            multiline
            rows={4}
            helperText="Каждая строка — отдельный вариант комментария"
          />

          <FormControl fullWidth margin="normal">
            <InputLabel>Порядок шаблонов</InputLabel>
            <Select
              value={formData.templateMode}
              onChange={(e) => setFormData({ ...formData, templateMode: e.target.value })}
              label="Порядок шаблонов"
            >
              <MenuItem value="random">Случайно — брать шаблоны вперемешку</MenuItem>
              <MenuItem value="sequential">По очереди — по порядку сверху вниз</MenuItem>
            </Select>
          </FormControl>

          <TextField
            fullWidth
            type="number"
            label="Макс. комментариев в день"
            value={formData.maxCommentsPerDay}
            onChange={(e) =>
              setFormData({ ...formData, maxCommentsPerDay: parseInt(e.target.value) })
            }
            margin="normal"
            inputProps={{ min: 1, max: 30 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Отмена</Button>
          <Button
            variant="contained"
            color="secondary"
            onClick={handleCreate}
            disabled={
              !formData.accountId || !formData.name || !formData.keywords || !formData.templates
            }
          >
            Создать
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
